import { useCart } from '@hooks/useCart/useCart';
import { useAddTopOffer } from '@hooks/useAddTopOffer/useAddTopOffer';
import { useAddEvItem } from '@hooks/useAddEvItem/useAddEvItem';
import { useDeleteTopOffer } from '@hooks/useDeleteTopOffer/useDeleteTopOffer';
import type { CartItemDTO, ShoppingCartDTO } from '@t/api.types';
import type { Plan } from '@t/productOffering.types';
import type { SelectPlanActions, UseSelectPlanActionsOptions } from './useSelectPlanActions.types';

const buildTopOfferPayload = (plan: Plan): CartItemDTO => ({
  action: 'add',
  quantity: 1,
  productOffering: { id: plan.planId, name: plan.name },
});

const findCartItemId = (cart: ShoppingCartDTO | null | undefined, offeringId: string): string | undefined =>
  cart?.cartItem?.find((item) => item.productOffering?.id === offeringId)?.id;

export const useSelectPlanActions = (
  sessionId: string,
  { onError, onPlanSelected }: UseSelectPlanActionsOptions = {}
): SelectPlanActions => {
  const cartQuery = useCart(sessionId);
  const addTopOfferM = useAddTopOffer(sessionId, {
    onSuccess: onPlanSelected ? () => onPlanSelected() : undefined,
    onError,
  });
  const addEvItemM = useAddEvItem(sessionId, {
    onSuccess: onPlanSelected ? () => onPlanSelected() : undefined,
    onError,
  });
  const deleteTopOfferM = useDeleteTopOffer(sessionId, { onError });

  const selectPlan = (plan: Plan) => {
    if (plan.type === 'ev') {
      addEvItemM.mutate(buildTopOfferPayload(plan));
    } else {
      addTopOfferM.mutate(buildTopOfferPayload(plan));
    }
  };

  const selectPlanAsync = (plan: Plan) =>
    plan.type === 'ev'
      ? addEvItemM.mutateAsync(buildTopOfferPayload(plan))
      : addTopOfferM.mutateAsync(buildTopOfferPayload(plan));

  const removePlan = (offeringId: string) => {
    const cartItemId = findCartItemId(cartQuery.data, offeringId);
    if (cartItemId) {
      deleteTopOfferM.mutate(cartItemId);
    }
  };

  const removePlanAsync = async (offeringId: string) => {
    const cartItemId = findCartItemId(cartQuery.data, offeringId);
    if (cartItemId) {
      await deleteTopOfferM.mutateAsync(cartItemId);
    }
  };

  const removeAllItems = async () => {
    // Intentionally NOT filtered: clearing the cart also removes the coupon line.
    const items = cartQuery.data?.cartItem ?? [];
    for (const item of items) {
      if (item.id) {
        await deleteTopOfferM.mutateAsync(item.id);
      }
    }
  };

  const isPending = addTopOfferM.isPending || addEvItemM.isPending || deleteTopOfferM.isPending;

  const pendingPlanId = addTopOfferM.isPending
    ? (addTopOfferM.variables?.productOffering?.id ?? null)
    : addEvItemM.isPending
      ? (addEvItemM.variables?.productOffering?.id ?? null)
      : null;

  return {
    selectPlan,
    selectPlanAsync,
    removePlan,
    removePlanAsync,
    removeAllItems,
    addTopOffer: addTopOfferM.mutate,
    addTopOfferAsync: addTopOfferM.mutateAsync,
    removeTopOffer: deleteTopOfferM.mutate,
    isPending,
    pendingPlanId,
  };
};
